/**
 * Invitations, both ends of them (3026, docs/INVITES.md).
 *
 * TWO AUDIENCES IN ONE SLICE, and they never overlap in a single session:
 *
 *   - THE OWNER invites by address, watches a PENDING list, resends and
 *     cancels. Everything here needs a session and a crew.
 *   - THE INVITED PERSON opens a link, reads what it says, and joins or
 *     declines. They may have no account at all, which is why `peek` and
 *     `accept` are the only actions in the store that run before sign-in.
 *
 * WHAT MARCUS ASKED FOR, and the shape of it: "you shouldn't be allowed to pick
 * the display name … just have to put in the email … Invites should not count
 * as joined." So there is no name field on the owner's side, no temp password
 * anywhere, and `pending` is deliberately NOT folded into the roster — it is a
 * separate list that the member count does not see.
 *
 * RESEND IS RE-INVITE. There is no separate action, because the server has no
 * separate operation: inviting an address that already has a live invitation
 * supersedes it and mints a new link. That is also the only truthful
 * implementation, since the previous token is stored as a hash and can never be
 * shown again — which is why the toast says the old link stopped working.
 */

import type { StateCreator } from 'zustand';

import type { InvitePeek, InviteSent, PendingInvite } from '../../repo/Repository.ts';
import { RepoError } from '../../repo/Repository.ts';
import { clearedCrewState } from './crews.ts';
import type { QdStore } from '../types.ts';

export interface InvitesSlice {
  /** Open invitations for the active crew, newest first. Owner-only. */
  pending: PendingInvite[];
  /** The address being typed into the invite field. */
  invEmail: string;
  /** An invite whose email could not be sent, so the owner must pass the link
   *  on themselves. Null on the ordinary path. */
  invSent: InviteSent | null;
  /** A write is open: minting, resending or cancelling. */
  invBusy: string;

  /** The token from `#invite=…`, held while the JOIN screen works. */
  joinToken: string;
  joinPeek: InvitePeek | null;
  /** The invitee's own choices, on the signed-out arm. */
  joinName: string;
  joinPw: string;
  /**
   * Typed again (Marcus, 2026-08-21: "nothing worse than setting the wrong
   * password because of a typo").
   *
   * It matters more here than anywhere else in the app. Every other password
   * field belongs to somebody who is already signed in and can try again from
   * a working session; this one is the ONLY credential a brand-new account
   * will ever have, set on a screen they reached from an email. Get it wrong
   * and the invitation is already burnt — there is no session, no old password
   * to authenticate a change, and no reset to send. The owner would have to
   * invite them again.
   */
  joinPw2: string;
  joinErr: string;
  joinBusy: boolean;
  /**
   * The session this screen was relying on turned out to be dead.
   *
   * A STATE, NOT AN ERROR, and specifically NOT the same thing as being signed
   * out. Whoever hits it WAS signed in a moment ago, so an account certainly
   * exists — showing them the create-an-account form would ask a member to
   * invent a second identity, which is what the first version of this repair
   * did (Marcus, 2026-08-21: "an existing user should never be prompted for
   * their name and to create a new password").
   *
   * So the screen asks them to sign in instead, keeps `joinToken`, and
   * `doLogin` brings them straight back here.
   */
  joinNeedsLogin: boolean;

  setInvEmail: (v: string) => void;
  loadPending: () => Promise<void>;
  sendInvite: (email?: string) => Promise<void>;
  cancelInvite: (email: string) => Promise<void>;
  closeInvSent: () => void;

  setJoinForm: (patch: { joinName?: string; joinPw?: string; joinPw2?: string }) => void;
  openJoin: (token: string) => Promise<void>;
  acceptJoin: () => Promise<void>;
  declineJoin: () => Promise<void>;
}

const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export const createInvitesSlice: StateCreator<QdStore, [], [], InvitesSlice> = (set, get) => ({
  pending: [],
  invEmail: '',
  invSent: null,
  invBusy: '',

  joinToken: '',
  joinPeek: null,
  joinName: '',
  joinPw: '',
  joinPw2: '',
  joinErr: '',
  joinBusy: false,
  joinNeedsLogin: false,

  setInvEmail: (invEmail) => set({ invEmail }),

  loadPending: async () => {
    try {
      set({ pending: await get().repo.listInvites() });
    } catch {
      // Same reasoning as `loadCrews`: a failed list is not worth a toast. The
      // crew still works, and blanking the section on one bad fetch would be
      // worse than leaving the previous answer up.
    }
  },

  /**
   * Mint an invitation. With no argument it takes the typed address, which is
   * the INVITE button; with one it is RESEND on an existing row.
   */
  sendInvite: async (email) => {
    const s = get();
    const addr = (email ?? s.invEmail).trim();
    const resend = email !== undefined;
    if (s.invBusy) return;
    if (!EMAIL_RE.test(addr)) {
      s.toast("✕ THAT DOESN'T LOOK LIKE AN EMAIL", true);
      return;
    }

    set({ invBusy: resend ? addr : 'new' });
    try {
      const sent = await s.repo.inviteMember(addr);
      set({
        invEmail: resend ? s.invEmail : '',
        // Only when we could not send it. On the emailed path there is no link
        // to show, deliberately — a copied link is bearer authority.
        invSent: sent.delivery === 'link' ? sent : null,
      });
      await get().loadPending();
      s.toast(
        sent.delivery === 'email'
          ? 'INVITE SENT — ' + addr.toUpperCase()
          : resend
            ? 'NEW LINK — THE OLD ONE NO LONGER WORKS'
            : 'INVITE READY — COPY THE LINK',
      );
    } catch (e) {
      const code = e instanceof RepoError ? e.code : '';
      s.toast(
        code === 'ALREADY_MEMBER'
          ? '✕ THEY ARE ALREADY IN THIS PARTY'
          : code === 'RATE_LIMITED'
            ? '✕ TOO MANY INVITES THIS HOUR — WAIT A WHILE'
            : "✕ COULDN'T SEND THAT INVITE — TRY AGAIN",
        true,
      );
    } finally {
      set({ invBusy: '' });
    }
  },

  cancelInvite: async (email) => {
    const s = get();
    if (s.invBusy) return;
    set({ invBusy: email });
    try {
      await s.repo.revokeInvite(email);
      await get().loadPending();
      s.toast('INVITE CANCELLED — ' + email.toUpperCase());
    } catch {
      s.toast("✕ COULDN'T CANCEL THAT INVITE — TRY AGAIN", true);
    } finally {
      set({ invBusy: '' });
    }
  },

  closeInvSent: () => set({ invSent: null }),

  setJoinForm: (patch) => set(patch),

  /**
   * Read the invitation and show the JOIN screen.
   *
   * `peek` never consumes anything, so this is safe to call again on a reload —
   * which matters, because the token lives in a URL fragment somebody may well
   * refresh.
   */
  openJoin: async (token) => {
    set({
      joinToken: token,
      joinPeek: null,
      joinName: '',
      joinPw: '',
      joinPw2: '',
      joinErr: '',
      joinNeedsLogin: false,
      joinBusy: true,
      screen: 'join',
    });
    try {
      set({ joinPeek: await get().repo.peekInvite(token) });
    } catch {
      // An unreadable invitation and an invalid one look the same to the
      // reader, which is also what the server intends.
      set({
        joinPeek: {
          state: 'invalid',
          crewName: '',
          crewSlug: '',
          inviterName: '',
          emailMasked: '',
          expiresAt: '',
        },
      });
    } finally {
      set({ joinBusy: false });
    }
  },

  acceptJoin: async () => {
    const s = get();
    if (s.joinBusy || !s.joinToken) return;
    const signedIn = Boolean(s.session);

    if (!signedIn) {
      if (!s.joinName.trim()) return set({ joinErr: 'CHOOSE A NAME' });
      if (s.joinPw.length < 8) return set({ joinErr: 'USE AT LEAST 8 CHARACTERS' });
      // Checked BEFORE the token is spent. A mismatch caught here costs a
      // retype; caught after would cost the whole invitation.
      if (s.joinPw !== s.joinPw2) return set({ joinErr: 'PASSWORDS DO NOT MATCH' });
    }

    set({ joinBusy: true, joinErr: '' });
    try {
      const res = await s.repo.acceptInvite({
        token: s.joinToken,
        ...(signedIn ? {} : { displayName: s.joinName.trim(), password: s.joinPw }),
      });

      if (res.createdAccount) {
        // They have an account now but no session — the server created it, it
        // did not sign them in. Do that with what they just chose, which is the
        // only moment the password is in hand.
        //
        // SIGN IN WITH THE ADDRESS, NOT THE NAME. This passed `joinName`, which
        // every other account in the product would resolve correctly, because
        // their login is derived from their name. An invited account's login is
        // the address the invitation was sent to and has nothing to do with the
        // name they picked — so the sign-in always failed, the catch below
        // re-read the invitation, found it (correctly) used, and told them so.
        // The join had worked; only the last step had not (Marcus, 2026-08-21).
        const identity = res.email ?? s.joinName.trim();
        try {
          /* THE RETURNED SESSION MUST BE STORED, and dropping it is what made
             a brand-new member's very first moment a BLANK SCREEN (Marcus,
             staging 2026-08-21).

             `repo.signIn` installs the tokens, so the account really was signed
             in — but `session` and `userId` stayed empty here, and two things
             downstream read them. `hydrateAfterAuth` opens with
             `if (!uid) return`, so nothing was fetched; and `headerVM` derives
             `authed` from `me()`, which is a lookup by `session` — so with the
             screen set to `tonight` and `authed` false, the frame matched no
             branch at all and painted nothing. A reload fixed it because
             `boot()` reads the session properly.

             `doLogin` has always done this; this path is the copy that forgot.
             Joining a SECOND crew never showed it: that arm is already signed
             in, so the fields were already right. */
          const session = await get().repo.signIn(identity, s.joinPw);
          set({
            session: session.userKey,
            userId: session.userId,
            // The baseline Profile's email form compares against, exactly as
            // `doLogin` sets it.
            pfAuthEmail: session.authEmail,
          });
        } catch {
          // The ACCOUNT EXISTS and the invitation is spent, so this must not
          // look like a failed join. Send them to sign in with the address they
          // now own, pre-filled.
          set({
            joinToken: '',
            joinPeek: null,
            joinName: '',
            joinPw: '',
            joinPw2: '',
            joinErr: '',
            sel: identity,
          });
          get().nav('login');
          get().toast('YOU ARE IN ' + res.crewName.toUpperCase() + ' — SIGN IN TO START');
          return;
        }
      }
      /* `clearedCrewState()` and not just the join fields: accepting an
         invitation SWITCHES the active crew server-side, so this is the back
         half of a crew switch and takes the same blank `switchCrew` does. The
         catalog especially — it is shaped around the crew that fetched it, and
         left standing it makes `hydrateAfterAuth` skip the refetch, joining
         the NEW crew's board against the OLD crew's catalog (the account-
         switch variant of this put a half-empty board in front of Marcus,
         2026-08-22). For a brand-new account everything here is empty anyway. */
      set({
        ...clearedCrewState(),
        activeCrew: null,
        joinToken: '',
        joinPeek: null,
        joinName: '',
        joinPw: '',
        joinPw2: '',
      });
      await get().hydrateAfterAuth();
      await get().loadCrews();
      // Accepting an invitation lands on MY GAMES like a sign-in (2026-09-03).
      get().nav('mine');
      get().toast('WELCOME TO ' + res.crewName.toUpperCase());
    } catch (e) {
      const code = e instanceof RepoError ? e.code : '';
      /* NAME_REQUIRED MEANS THE SESSION DIED UNDER US, and it is worth
         decoding rather than lumping in with the generic failure.

         The server asks for a name only on its SIGNED-OUT arm, and this action
         refuses to call it without one when it believes it is signed out — so
         receiving it can mean exactly one thing: the screen thought there was a
         session and the server resolved none. Marcus hit it on staging
         (2026-08-21): he had signed out of that account in another tab, GoTrue
         answered the stale token with `session_not_found`, and the JOIN screen
         said "COULDN'T JOIN — TRY AGAIN" — advice that could never work, on the
         one screen where the invitation is the only way in.

         Clearing the store's session is the whole repair: `joinVM.needsAccount`
         flips to true, the screen re-renders with the name and password fields
         AND the "already have an account? log in" door, and either path now
         reaches an honest answer — including EMAIL_TAKEN above, which tells
         somebody who does have an account exactly what to do. */
      const staleSession = code === 'NAME_REQUIRED';
      set({
        /* SIGN THEM IN AGAIN — do NOT offer to create an account.
           An account certainly exists: they were signed in moments ago. The
           first version of this repair dropped them into the create-an-account
           form, which asked a member to invent a second identity on an address
           that already has one (Marcus, 2026-08-21). `joinNeedsLogin` is what
           keeps the form away; the token is kept so `doLogin` can return them
           here rather than making them find the email again. */
        ...(staleSession ? { session: '', userId: '', joinNeedsLogin: true } : null),
        joinErr:
          code === 'EMAIL_TAKEN'
            ? 'YOU ALREADY HAVE AN ACCOUNT ON THIS EMAIL. LOG IN, THEN OPEN THIS LINK AGAIN.'
            : code === 'ADDRESS_MISMATCH'
              ? 'THIS INVITE WAS SENT TO A DIFFERENT ADDRESS.'
              : code === 'INVITE_CLOSED'
                ? 'THIS INVITE IS NO LONGER OPEN.'
                : staleSession
                  ? ''
                  : "COULDN'T JOIN — TRY AGAIN",
      });
      // Re-read it: whatever refused us has probably changed what the screen
      // should be saying.
      try {
        set({ joinPeek: await get().repo.peekInvite(s.joinToken) });
      } catch {
        /* leave the previous answer up */
      }
    } finally {
      set({ joinBusy: false });
    }
  },

  declineJoin: async () => {
    const s = get();
    if (s.joinBusy || !s.joinToken) return;
    set({ joinBusy: true });
    try {
      await s.repo.declineInvite(s.joinToken);
      set({ joinPeek: await get().repo.peekInvite(s.joinToken) });
    } catch {
      get().toast("✕ COULDN'T DECLINE THAT INVITE", true);
    } finally {
      set({ joinBusy: false });
    }
  },
});
